import axios from "axios";

/**
 * Generate invoice number based on settings
 */
export const generateInvoiceNumber = (prefix, includeYear, counter) => {
  const year = includeYear ? new Date().getFullYear() : "";
  const formattedCounter = String(counter).padStart(5, "0");
  return `${prefix}${year ? year + "-" : ""}${formattedCounter}`;
};

/**
 * Calculate due date based on payment terms
 */
export const calculateDueDate = (invoiceDate, terms) => {
  const date = new Date(invoiceDate);

  switch (terms) {
    case "on_receipt":
      return date.getTime();
    case "15":
      date.setDate(date.getDate() + 15);
      return date.getTime();
    case "45":
      date.setDate(date.getDate() + 45);
      return date.getTime();
    case "90":
      date.setDate(date.getDate() + 90);
      return date.getTime();
    case "custom":
      return null; // User will select custom date
    default:
      return date.getTime();
  }
};

/**
 * Get terms API value from localized term string
 */
export const getTermsApiValue = (term, locale) => {
  // Map localized terms to API values
  const termsMap = {
    Immediate: "IMMEDIATE",
    فوري: "IMMEDIATE",
    "15 days": "NET_15",
    "15 يوم": "NET_15",
    "45 days": "NET_45",
    "45 يوم": "NET_45",
    "90 days": "NET_90",
    "90 يوم": "NET_90",
    Custom: "CUSTOM",
    مخصص: "CUSTOM",
  };

  return termsMap[term] || "IMMEDIATE";
};

/**
 * Calculate discount amount based on type
 */
export const calculateDiscountAmount = (
  subtotal,
  discountValue,
  discountType
) => {
  if (!discountValue || discountValue === 0) return 0;

  if (discountType === "PERCENTAGE") {
    return subtotal * (discountValue / 100);
  }

  // FIXED amount
  return Math.min(discountValue, subtotal); // Ensure discount doesn't exceed subtotal
};

/**
 * Calculate invoice totals
 */
export const calculateTotals = (
  items,
  discountEnabled,
  discountValue,
  discountType,
  taxEnabled = false,
  taxRate = 0,
  taxType = "PERCENTAGE"
) => {
  // Calculate subtotal from items
  const subtotal = items.reduce((sum, item) => {
    const itemTotal = (item.quantity || 0) * (item.pricePerUnit || 0);
    return sum + itemTotal;
  }, 0);

  // Calculate discount
  const discount = discountEnabled
    ? calculateDiscountAmount(subtotal, discountValue, discountType)
    : 0;

  // Calculate amount after discount
  const afterDiscount = Math.max(0, subtotal - discount);

  // Calculate tax on the discounted amount
  let tax = 0;
  if (taxEnabled && taxRate > 0) {
    if (taxType === "PERCENTAGE") {
      tax = (afterDiscount * taxRate) / 100;
    } else {
      tax = taxRate; // Fixed tax amount
    }
  }

  // Calculate final total
  const total = afterDiscount + tax;

  return {
    subtotal,
    discount,
    tax,
    afterDiscount,
    total,
  };
};

/**
 * Validate item before adding
 */
export const validateItem = (item) => {
  if (!item.product || !item.product.id) {
    return { valid: false, message: "Please select a product" };
  }

  if (!item.quantity || item.quantity <= 0) {
    return { valid: false, message: "Quantity must be greater than 0" };
  }

  if (!item.pricePerUnit || item.pricePerUnit < 0) {
    return { valid: false, message: "Price must be 0 or greater" };
  }

  return { valid: true };
};

/**
 * Format number based on locale
 */
export const formatNumber = (num, locale) => {
  if (num === null || num === undefined) return "0";

  return new Intl.NumberFormat(locale === "ar" ? "ar-EG" : "en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(num);
};

/**
 * Format currency with locale
 */
export const formatCurrency = (amount, locale, currency = "EGP") => {
  if (!amount && amount !== 0) return "—";

  const formatted = formatNumber(amount, locale);

  // Map currency codes to locale-specific labels
  const currencyLabels = {
    EGP: locale === "ar" ? "ج.م" : "EGP",
    USD: locale === "ar" ? "$" : "$",
    SAR: locale === "ar" ? "ر.س" : "SAR",
  };

  return `${formatted} ${currencyLabels[currency] || currency}`;
};

/**
 * Format date based on locale
 */
export const formatDate = (date, locale) => {
  if (!date) return "";

  const dateObj = date instanceof Date ? date : new Date(date);

  return dateObj.toLocaleDateString(locale === "ar" ? "ar-EG" : "en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

/**
 * Get next invoice counter from API
 */
export const getNextInvoiceCounter = async (farmId, token) => {
  try {
    const response = await axios.get(`api/invoice/nextCounter/${farmId}`, {
      headers: { Authorization: token },
    });

    console.log("Next counter API response:", response.data);

    // API returns counter in items[0].next_counter structure
    if (
      response.data &&
      response.data.items &&
      response.data.items.length > 0
    ) {
      const counter = parseInt(response.data.items[0].next_counter);
      return isNaN(counter) ? 1 : counter;
    }

    return 1; // Default to 1 if no counter found
  } catch (error) {
    console.error("Error fetching next invoice counter:", error);
    return 1;
  }
};

/**
 * Validate invoice number uniqueness
 */
export const validateInvoiceNumber = async (farmId, invoiceNumber, token) => {
  try {
    const response = await axios.post(
      "api/invoice/validateNumber",
      {
        farm_id: farmId,
        invoice_number: invoiceNumber,
      },
      { headers: { Authorization: token } }
    );

    // Check if invoice number is valid (doesn't exist)
    if (response.data) {
      if (response.data.isValid !== undefined) {
        return response.data.isValid;
      }
      if (response.data.valid !== undefined) {
        return response.data.valid;
      }
      if (response.data.exists !== undefined) {
        return !response.data.exists; // Invert if API returns "exists"
      }
    }

    return true; // Default to valid if uncertain
  } catch (error) {
    console.error("Error validating invoice number:", error);
    // If validation fails, assume it's valid to not block user
    return true;
  }
};

/**
 * Prepare invoice data for API submission
 */
export const prepareInvoiceData = (formData, userData) => {
  const {
    invoiceType,
    selectedEntity,
    selectedWarehouse,
    invoiceNumber,
    invoiceCounter,
    date,
    terms,
    dueDate,
    items,
    discountEnabled,
    discountValue,
    discountType,
    taxEnabled,
    taxRate,
    taxType,
    totals,
    companyInfo,
  } = formData;

  // Prepare invoice items
  const invoiceItems = items.map((item) => {
    const itemData = {
      inventoryItemDetailId: item.product.id,
      quantity: item.quantity,
      pricePerUnit: item.pricePerUnit,
      totalAmount: item.quantity * item.pricePerUnit,
    };

    // For both purchase and sale invoices, add locationId per item
    if (item.locationId) {
      itemData.locationId = item.locationId;
    }

    return itemData;
  });

  // Build invoice object matching backend InvoiceRequest model
  const invoiceData = {
    tenantId: userData.Tenant?.id,
    farmId: userData.Farm?.id,
    type: invoiceType, // "purchase" or "sale"
    number: invoiceNumber,
    invoiceCounter: invoiceCounter || 0,
    invoiceDate: new Date(date).getTime(), // Keep as number, not string
    // Android sends subtotal (before discount) as totalAmount when discount exists
    totalAmount:
      discountEnabled && discountValue > 0 ? totals.subtotal : totals.total,
    items: invoiceItems,
    // Always include these fields to avoid null pointer exceptions
    withDiscount: discountEnabled || false,
    amountPaid: 0,
    balanceAmount: totals.total,
    source: "web", // Identify as web source
    fieldId: null, // Optional field
    accountId: null, // Optional field
    image: null, // Will be set if images are uploaded
  };

  // Add entity based on type
  if (invoiceType === "purchase") {
    invoiceData.supplierId = selectedEntity.id;
    // locationId is now per item (handled above in invoiceItems)
  } else {
    invoiceData.clientId = selectedEntity.id;
    // locationId is per item (handled above in invoiceItems)
  }

  // If entity has an accountId, use it
  if (selectedEntity.accountId) {
    invoiceData.accountId = selectedEntity.accountId;
  }

  // Add terms and due date
  if (terms) {
    invoiceData.terms = terms;
    const calculatedDueDate = dueDate || calculateDueDate(date, terms);
    if (calculatedDueDate) {
      invoiceData.dueDate = Number(calculatedDueDate); // Ensure it's a number
    }
  }

  // Add discount if enabled
  if (discountEnabled && discountValue > 0) {
    invoiceData.withDiscount = true;
    invoiceData.discountRate = discountValue;
    // Backend expects lowercase enum values: "fixed" or "percentage"
    invoiceData.discountType = discountType.toLowerCase(); // Convert "FIXED"/"PERCENTAGE" to "fixed"/"percentage"
    // discountAmount: totals.discount // REMOVED - not in backend model
  } else {
    // Explicitly set discount fields to null/false when no discount
    invoiceData.withDiscount = false;
    invoiceData.discountRate = null;
    invoiceData.discountType = null;
  }

  // Add tax if enabled
  if (taxEnabled && taxRate > 0) {
    invoiceData.taxRate = taxRate;
    invoiceData.taxType = taxType; // "PERCENTAGE" or "FIXED"
    invoiceData.taxAmount = totals.tax;
  }

  // Add company info if available
  if (companyInfo && companyInfo.companyName) {
    invoiceData.companyInfo = {
      name: companyInfo.companyName,
      taxNumber: companyInfo.taxNumber,
      commercialNumber: companyInfo.commercialNumber,
      address: companyInfo.companyAddress,
      phone: companyInfo.companyPhone,
      showTaxNumber: companyInfo.showTaxNumber,
      showCommercialNumber: companyInfo.showCommercialNumber,
    };
  }

  return invoiceData;
};

/**
 * Filter entities based on invoice type
 */
export const filterEntitiesByType = (entities, invoiceType) => {
  if (invoiceType === "purchase") {
    // For purchase invoices, show suppliers
    return entities.filter(
      (e) => e.accountType === "SUPPLIER" || e.accountType === "BOTH"
    );
  } else {
    // For sale invoices, show clients
    return entities.filter(
      (e) => e.accountType === "CLIENT" || e.accountType === "BOTH"
    );
  }
};

/**
 * Filter products based on invoice type
 */
export const filterProductsByType = (products, invoiceType) => {
  // Show all products for both purchase and sale invoices
  return products;
};

/**
 * Get invoice state label
 */
export const getInvoiceStateLabel = (invoice, t) => {
  const { totalAmount, amountPaid, balanceAmount } = invoice;

  if (totalAmount === amountPaid && balanceAmount === 0) {
    return t("Paid");
  } else if (amountPaid > 0 && amountPaid < totalAmount) {
    return t("Partially Paid");
  } else {
    return t("Not Paid");
  }
};

/**
 * Get invoice state class for styling
 */
export const getInvoiceStateClass = (invoice) => {
  const { totalAmount, amountPaid, balanceAmount } = invoice;

  if (totalAmount === amountPaid && balanceAmount === 0) {
    return "paid";
  } else if (amountPaid > 0 && amountPaid < totalAmount) {
    return "partially_paid";
  } else {
    return "not_paid";
  }
};
